import { gameKeyMatchesUrl } from '@/services/analyticsGameKey';
import { normalizeSport } from '@/stores/marketFrameNormalization';
import { useMarketStore } from '@/stores/marketStore';
import { MULTI_OUTCOME_SPORTS, SPORT_TABS } from '@/views/ValueDashboard/types';
import type { GameEventRow, GameRow, MultiOutcomeEvent, SeriesOutcome, Sport } from '@/views/ValueDashboard/types';
import { multiOutcomeRoles } from '@/views/ValueDashboard/multiOutcome';
import { synthesizeFromSpread, synthesizeFromTotal } from '@/views/SpreadsTotals/synthesize';

export interface GameUrlParts {
  sport?: string;
  date?: string;
  teamA?: string;
  teamB?: string;
  gameNo?: string;
}

const ALL_SPORTS = SPORT_TABS.map(tab => tab.id as Sport);
const ALL_DATES_AND_STATUSES = { dateFilter: 'all', statusFilter: 'all' } as const;

/** An explicit route already identifies the sport. Do not build unrelated
 * inventories to rediscover it on every feed publication. Legacy sport-less
 * and unknown routes retain their old search order and exact key matching.
 * Tennis also keeps the old scan: tour-less keys and ITF pairing can route a
 * frame between ATP and WTA, so the route alone is not sufficient evidence.
 * Normalize with the same vocabulary as the matcher (including CFB/ncaaf).
 * No result cache: quotes, new/removed lines, and identity corrections remain
 * visible on the existing publication clock. */
function lookupSports(url: GameUrlParts): readonly Sport[] {
  const sport = normalizeSport(url.sport?.trim().toLowerCase() ?? '');
  if (sport === 'atp' || sport === 'wta') return ALL_SPORTS;
  return sport && ALL_SPORTS.includes(sport) ? [sport] : ALL_SPORTS;
}

export function findGameRow(url: GameUrlParts): GameRow | null {
  const store = useMarketStore.getState();
  for (const sport of lookupSports(url)) {
    const hit = store.getGames(sport, 'moneyline', ALL_DATES_AND_STATUSES)
      .find(game => gameKeyMatchesUrl(game.key, url));
    if (hit) return hit;
  }
  return null;
}

interface MultiOutcomeGame {
  event: MultiOutcomeEvent;
  away?: SeriesOutcome;
  home?: SeriesOutcome;
  draw?: SeriesOutcome;
}

export function findMultiOutcomeGame(url: GameUrlParts): MultiOutcomeGame | null {
  const store = useMarketStore.getState();
  for (const sport of lookupSports(url)) {
    if (!MULTI_OUTCOME_SPORTS.has(sport)) continue;
    const event = store.getMultiOutcomeEvents(sport).find(game => gameKeyMatchesUrl(game.key, url));
    if (event) return { event, ...multiOutcomeRoles(event) };
  }
  return null;
}

export function findGameEvent(url: GameUrlParts): GameEventRow | null {
  const store = useMarketStore.getState();
  for (const sport of lookupSports(url)) {
    const hit = store.getGameEvents(sport, ALL_DATES_AND_STATUSES)
      .find(game => gameKeyMatchesUrl(game.key, url));
    if (hit) return hit;
  }
  return null;
}

/** Reuse the event the page resolved in this render; rebuilding its complete
 * spread/total inventory a second time cannot provide a fresher snapshot.
 * Preserve exact ticker selection and the caller's existing missing-line
 * behavior. This helper neither caches a result nor changes execution gates. */
export function findLiveSynthGameRow(synthKey: string, event: GameEventRow | null): GameRow | null {
  const parts = synthKey.split('|');
  if (parts.length < 4 || !event) return null;
  const ticker = parts[parts.length - 1];
  const kind = parts[parts.length - 2];
  if (kind === 'spread') {
    const line = event.spreads.find(s => s.kalshi?.marketTicker === ticker || s.polymarket?.marketTicker === ticker);
    return line ? synthesizeFromSpread(event, line) : null;
  }
  if (kind === 'total') {
    const line = event.totals.find(t => t.kalshi?.marketTicker === ticker || t.polymarket?.marketTicker === ticker);
    return line ? synthesizeFromTotal(event, line) : null;
  }
  return null;
}
